import {
  buildEventsFilter,
  ensureValidProfileId,
  ensureValidProfileEventId,
  groupInterestsListByProfileField,
  normalizeFeedback,
  normalizeInterests,
} from '../utils'

export const list = {
  pathname: '/profiles',
  search: () => {
    // TODO: Will update later with http://b/316386851. Hardcoded for now since we will not do pagination until April GA release
    return { pageSize: 1000 }
  }
}

export const get = {
  pathname: ({ id }) => `/profiles/${ensureValidProfileId(id)}`
}

export const create = {
  pathname: list.pathname,
  method: 'POST',
  headers: {
    'content-type': 'application/json',
  },
  body: ({ profile }) => {
    if (typeof profile !== 'object') {
      throw new Error('profiles.create requires `profile` param')
    }
    const { interests, ...rest } = profile
    if (interests?.length > 0) {
      const normalizedInterests = groupInterestsListByProfileField(interests)
      profile = { ...rest, ...normalizedInterests }
      return { profile }
    } else {
      return { profile: { ...rest } }
    }
  }
}

export const update = {
  method: 'PATCH',
  pathname: get.pathname,
  headers: {
    'content-type': 'application/json',
  },
  search: ({ profile = {}, updateMask = true }) => {
    // if updateMask is explicitly falsy, return no search
    if (!updateMask) {
      return
    }
    // if updateMask is a string, respect it
    if (typeof updateMask === 'string') {
      return { updateMask }
    }
    // if updateMask is an array, expect strings and join it
    if (Array.isArray(updateMask)) {
      return { updateMask: updateMask.join(',') }
    }
    // otherwise assume the update is partial and derive updateMask from
    // the update payload
    updateMask = Object.keys(profile).sort().join(',')
    return updateMask ? { updateMask } : undefined
  },
  body: ({ profile }) => {
    if (typeof profile !== 'object') {
      throw new Error('profiles.update requires `profile` param')
    }
    return { profile }
  }
}

export const remove = {
  method: 'DELETE',
  pathname: get.pathname,
}

export const addInterest = {
  method: 'POST',
  headers: {
    'content-type': 'application/json',
  },
  pathname: vars => `${get.pathname(vars)}:addInterest`,
  body: ({ interest, id }) => {
    const add = normalizeInterests(interest)
    if (!add.length) {
      throw new Error(
        'profiles.addInterest failed to build body, expected interest to be valid object IDs ' +
        'or interest objects'
      )
    }
    return { add }
  }
}

export const removeInterest = {
  method: 'POST',
  headers: {
    'content-type': 'application/json',
  },
  pathname: vars => `${get.pathname(vars)}:removeInterest`,
  body: ({ interest, id }) => {
    const remove = normalizeInterests(interest)
    if (!remove.length) {
      throw new Error(
        'profiles.removeInterest failed to build body, expected interest to be valid object IDs ' +
        'or interest objects'
      )
    }
    return { remove }
  }
}

export const setRecommendationFeedback = {
  method: 'POST',
  headers: {
    'content-type': 'application/json',
  },
  pathname: vars => `${get.pathname(vars)}:setRecommendationFeedback`,
  body: ({ interest, feedback, id }) => {
    // the api expects just 1 interest object, not an array list of interests
    const normalizedInterest = normalizeInterests(interest)[0]
    const normalizedFeedback = normalizeFeedback[feedback]

    if (!normalizedInterest || typeof normalizedInterest !== 'object') {
      throw new Error(
        'profiles.setRecommendationFeedback failed to build body, expected interest to be valid object IDs ' +
        'or interest objects'
      )
    }
    if (!normalizedFeedback) {
      throw new Error('profiles.setRecommendationFeedback failed to build body, expected feedback to be one of "like", "dislike", or "neutral"')
    }
    return { interest: normalizedInterest, feedback: normalizedFeedback }
  }
}

export const listEvents = {
  pathname: ({ profileId }) => `${get.pathname({ id: profileId })}/events`,
  search: vars => {
    const eventsFilter = buildEventsFilter(vars)
    const searchParams = {
      filter: eventsFilter,
      pageSize: 25,
      ...(vars.pageToken && { pageToken: vars.pageToken })
    }
    return eventsFilter ? searchParams : {}
  }
}

export const getEvent = {
  pathname: ({ profileId, id }) => `${get.pathname({ id: profileId })}/events/${ensureValidProfileEventId(id)}`,
}
